import {
    createContext,
    useCallback,
    useContext,
    useEffect,
    useLayoutEffect,
    useMemo,
    useState,
} from 'react';
import PropTypes from 'prop-types';
import { AppLanguages } from '../Utils/AppLanguages.jsx';
import { setUiLanguage, readLanguageFromUrl, consumeLanguageFromUrl } from '../Utils/uiLocale.js';
import en from '../locals/en.json';
import tr from '../locals/tr.json';
import ar from '../locals/ar.json';
import fa from '../locals/fa.json';
import ru from '../locals/ru.json';
import { useAuth } from './AuthContext.js';
import { profileAPI } from '../api/api.js';

const LanguageContext = createContext(null);

const CATALOGS = {
    en,
    tr,
    ar,
    fa,
    ru,
};

const DEFAULT_LANGUAGE = 'en';
const STORAGE_KEY = 'appLanguage';
const RTL_LANGUAGES = new Set(['ar', 'fa']);

/** Languages that have a translation catalog. */
export const AVAILABLE_LANGUAGES = AppLanguages.filter((lang) => CATALOGS[lang.code]);

function resolveInitialLanguage() {
    // Public marketing site → dashboard handoff (?lang=fa)
    const fromUrl = readLanguageFromUrl();
    if (fromUrl && CATALOGS[fromUrl]) {
        consumeLanguageFromUrl(fromUrl);
        return fromUrl;
    }
    const saved = localStorage.getItem(STORAGE_KEY);
    if (saved && CATALOGS[saved]) return saved;
    return DEFAULT_LANGUAGE;
}

function getByPath(obj, path) {
    if (!obj || !path) return undefined;
    return String(path)
        .split('.')
        .reduce((acc, key) => (acc == null ? undefined : acc[key]), obj);
}

function interpolate(value, vars) {
    if (typeof value !== 'string' || !vars || typeof vars !== 'object') return value;
    return value.replace(/\{\{(\w+)\}\}/g, (_, key) => {
        const next = vars[key];
        return next == null ? '' : String(next);
    });
}

export const useLanguage = () => {
    const context = useContext(LanguageContext);
    if (!context) {
        throw new Error('useLanguage must be used within a LanguageProvider');
    }
    return context;
};

export const LanguageProvider = ({ children }) => {
    const [language, setLanguageState] = useState(resolveInitialLanguage);
    const { isAuthenticated, user, updateUser } = useAuth();
    setUiLanguage(language);

    // Emails use the saved account language. Keep it aligned with the dashboard language.
    useEffect(() => {
        if (!isAuthenticated || !user?._id) return undefined;
        const saved = String(user.profile?.language || '')
            .trim()
            .toLowerCase()
            .split(/[-_]/)[0];
        if (saved === language) return undefined;
        // Leave languages the dashboard does not translate (for example es, fr, de, ps).
        if (saved && !CATALOGS[saved]) return undefined;

        let cancelled = false;
        profileAPI
            .updateProfile({ profile: { language } })
            .then((response) => {
                if (!cancelled && response?.data?.admin) updateUser(response.data.admin);
            })
            .catch(() => {});

        return () => {
            cancelled = true;
        };
    }, [isAuthenticated, user?._id, user?.profile?.language, language, updateUser]);

    useLayoutEffect(() => {
        document.documentElement.lang = language;
        document.documentElement.dir = RTL_LANGUAGES.has(language) ? 'rtl' : 'ltr';
        localStorage.setItem(STORAGE_KEY, language);
    }, [language]);

    const setLanguage = useCallback((nextLanguage) => {
        const code = String(nextLanguage || '')
            .trim()
            .toLowerCase();
        if (!CATALOGS[code]) return;
        setLanguageState(code);
    }, []);

    const t = useCallback(
        (key, vars) => {
            const fromActive = getByPath(CATALOGS[language], key);
            const fromFallback = getByPath(CATALOGS[DEFAULT_LANGUAGE], key);
            const raw = fromActive ?? fromFallback ?? key;
            return interpolate(raw, vars);
        },
        [language],
    );

    const value = useMemo(
        () => ({
            language,
            setLanguage,
            t,
            languages: AVAILABLE_LANGUAGES,
            currentLanguage:
                AVAILABLE_LANGUAGES.find((lang) => lang.code === language) ||
                AVAILABLE_LANGUAGES[0],
        }),
        [language, setLanguage, t],
    );

    return (
        <LanguageContext.Provider value={value}>
            {children}
        </LanguageContext.Provider>
    );
};

LanguageProvider.propTypes = {
    children: PropTypes.node,
};
